import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, Loader2 } from "lucide-react";

const SPORT = { basketball: "Basketball", flag_football: "Flag football", other: "Other / not sure" };

const Stat = ({ label, value, testId }) => (
  <div className="border-2 border-[#D3CFC0] bg-white p-4" data-testid={testId}>
    <span className="kicker text-[#6E6E6E]">{label}</span>
    <p className="mt-1 font-head text-3xl text-[#1E1E1E]">{value}</p>
  </div>
);

export default function RookieAdmin({ api }) {
  const [d, setD] = useState(null);
  const [showTest, setShowTest] = useState(false);
  const qs = `?include_test=${showTest}`;
  const load = () => api.get(`/admin/rookie/stats${qs}`).then(({ data }) => setD(data)).catch(() => toast.error("Couldn't load Rookie Guide stats."));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showTest]);

  const exportCsv = async () => {
    try {
      const { data } = await api.get(`/admin/rookie/leads.csv${qs}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "rookie-guide-leads.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };
  const remove = async (l) => {
    if (!window.confirm(`Delete the lead ${l.email}?`)) return;
    try { await api.delete(`/admin/rookie/leads/${l.id}`); load(); } catch { toast.error("Delete failed."); }
  };
  const flag = async (l) => {
    try { await api.patch(`/admin/rookie/leads/${l.id}`, { is_test: !l.is_test }); load(); } catch { toast.error("Update failed."); }
  };

  if (!d) return <Loader2 className="mt-8 animate-spin" />;
  return (
    <div className="mt-8 space-y-6" data-testid="rookie-admin">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Total leads" value={d.total} testId="rookie-stat-total" />
        <Stat label="Sideline opt-in" value={`${d.opt_in_rate}%`} testId="rookie-stat-optin" />
        <Stat label="Downloads" value={d.downloads} testId="rookie-stat-downloads" />
        <Stat label="Starter Kit clicks" value={d.starter_kit_clicks} testId="rookie-stat-kit-clicks" />
        <div className="border-2 border-[#D3CFC0] bg-white p-4 col-span-2 lg:col-span-1" data-testid="rookie-stat-sports">
          <span className="kicker text-[#6E6E6E]">By sport</span>
          {Object.entries(d.by_sport).map(([k, v]) => <p key={k} className="text-sm text-[#1E1E1E]">{SPORT[k]}: <b>{v}</b></p>)}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-head uppercase text-xl text-[#1E1E1E]">Leads</h3>
        <label className="flex items-center gap-2 text-sm text-[#3A3A3A] cursor-pointer mr-auto ml-4" data-testid="rookie-show-test-label">
          <input type="checkbox" checked={showTest} onChange={(e) => setShowTest(e.target.checked)} className="h-4 w-4 accent-[#DB9130]" data-testid="rookie-show-test" />
          Show internal/test leads{!showTest && d.hidden_test_leads > 0 ? ` (${d.hidden_test_leads} hidden)` : ""}
        </label>
        <button onClick={exportCsv} data-testid="rookie-export-csv"
          className="inline-flex items-center gap-2 bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm px-4 py-2 hover:bg-[#D6922C]">
          <Download size={14} /> Export CSV
        </button>
      </div>
      <div className="overflow-x-auto border-2 border-[#D3CFC0] bg-white">
        <table className="w-full text-sm" data-testid="rookie-leads-table">
          <thead className="bg-[#E7E3D9] text-left">
            <tr>{["", "Email", "Name", "Sport", "Role", "Sideline", "Source", "Downloads", "Kit clicks", "Date"].map((h) => <th key={h} className="px-3 py-2 font-head uppercase text-xs whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {d.leads.length === 0 && <tr><td colSpan={10} className="px-3 py-4 text-[#6E6E6E]" data-testid="rookie-leads-empty">No leads yet.</td></tr>}
            {d.leads.map((l) => (
              <tr key={l.id} className="border-t border-[#E7E3D9]" data-testid={`rookie-lead-${l.id}`}>
                <td className="px-3 py-2"><button onClick={() => remove(l)} className="text-[#6E6E6E] hover:text-[#B3261E]" data-testid={`rookie-lead-delete-${l.id}`}><Trash2 size={14} /></button></td>
                <td className="px-3 py-2 break-all">
                  {l.email}
                  {l.is_test && <span className="ml-2 kicker border border-current px-1 text-[#B3261E]" data-testid={`rookie-lead-test-badge-${l.id}`}>Test</span>}
                  {!l.email.endsWith("@voluntoldhq.com") && (
                    <button onClick={() => flag(l)} className="ml-2 text-xs underline text-[#6E6E6E]" data-testid={`rookie-lead-flag-${l.id}`}>
                      {l.is_test ? "Unmark test" : "Mark as test"}
                    </button>
                  )}
                </td><td className="px-3 py-2">{l.first_name || ""}</td>
                <td className="px-3 py-2">{SPORT[l.sport] || l.sport}</td><td className="px-3 py-2">{l.role || ""}</td>
                <td className="px-3 py-2">{l.newsletter_opt_in ? "Yes" : "No"}</td><td className="px-3 py-2">{l.source}</td>
                <td className="px-3 py-2">{l.downloads || 0}</td><td className="px-3 py-2">{l.starter_kit_clicks || 0}</td>
                <td className="px-3 py-2 whitespace-nowrap">{(l.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
